import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Bot, Plus, Send, User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import Header from '../components/Header';
import { portalJson, portalFetch, getMe, ClientAuthError } from '../components/portal/portalApi';
import { inputStyle } from '../components/quote/quoteStyles';

const Bubble = ({ role, content, streaming }) => (
  <div data-testid={`chat-message-${role}`} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexDirection: role === 'user' ? 'row-reverse' : 'row' }}>
    <span style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: role === 'user' ? 'var(--border-medium)' : 'var(--brand-primary)', color: role === 'user' ? 'var(--text-primary)' : '#fff' }}>
      {role === 'user' ? <User size={16} /> : <Bot size={16} />}
    </span>
    <div style={{ maxWidth: '78%', padding: '0.85rem 1.1rem', borderRadius: '12px', whiteSpace: role === 'user' ? 'pre-wrap' : 'normal', lineHeight: 1.6, fontSize: '0.95rem', backgroundColor: role === 'user' ? 'var(--brand-primary)' : 'var(--bg-card)', color: role === 'user' ? '#fff' : 'var(--text-primary)', border: role === 'user' ? 'none' : '1px solid var(--border-medium)' }}>
      {role === 'assistant' ? <div className="chat-markdown"><ReactMarkdown>{content}</ReactMarkdown></div> : content}{streaming && <span style={{ opacity: 0.6 }}>▍</span>}
    </div>
  </div>
);

const AssistantPage = () => {
  const [user, setUser] = useState(undefined);
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);
  const streamingRef = useRef(false);

  useEffect(() => { getMe().then(setUser); }, []);
  useEffect(() => {
    if (!user) return;
    portalJson('/api/portal/chat/sessions').then((list) => { setSessions(list); if (list[0]) setActive(list[0].id); }).catch((e) => setError(e.message));
  }, [user]);
  useEffect(() => {
    if (streamingRef.current) return;
    if (!active) { setMessages([]); return; }
    portalJson(`/api/portal/chat/sessions/${active}/messages`).then(setMessages).catch((e) => setError(e.message));
  }, [active]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const newSession = async () => {
    setError('');
    try {
      const s = await portalJson('/api/portal/chat/sessions', { method: 'POST' });
      setSessions((prev) => [s, ...prev]); setActive(s.id);
      return s.id;
    } catch (e) {
      setError(e.message);
      return null;
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    setInput(''); setStreaming(true); streamingRef.current = true; setError('');
    let sessionId = active || (await newSession());
    if (!sessionId) { setStreaming(false); streamingRef.current = false; return; }
    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    try {
      const res = await portalFetch(`/api/portal/chat/sessions/${sessionId}/messages`, { method: 'POST', body: JSON.stringify({ text }) });
      if (res.status === 401) throw new ClientAuthError('Please sign in');
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Unable to send'); }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n'); buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') continue;
          const evt = JSON.parse(payload);
          if (evt.error) setError(evt.error);
          if (evt.delta) setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last && last.role === 'assistant') copy[copy.length - 1] = { ...last, content: last.content + evt.delta };
            else copy.push({ role: 'assistant', content: evt.delta });
            return copy;
          });
        }
      }
      setSessions((prev) => prev.map((s) => (s.id === sessionId && s.title === 'New conversation' ? { ...s, title: text.slice(0, 60) } : s)));
    } catch (err) {
      setError(err.message);
      setMessages((m) => m.slice(0, -1));
    } finally {
      setStreaming(false);
      streamingRef.current = false;
    }
  };

  if (user === null) return <div><Header /><section style={{ padding: '160px 0' }} className="container"><p data-testid="assistant-signin-prompt">Please <Link to="/portal" style={{ color: 'var(--brand-primary)' }}>sign in to your portal</Link> to use the assistant.</p></section></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Helmet><title>AI Document Assistant | Legal Document Specialist</title><meta name="robots" content="noindex" /></Helmet>
      <Header />
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '260px minmax(0, 1fr)', paddingTop: '112px', backgroundColor: 'var(--bg-page)', minHeight: 0 }} className="assistant-layout">
        <aside style={{ borderRight: '1px solid var(--border-medium)', padding: '1.25rem', overflowY: 'auto' }}>
          <button type="button" data-testid="chat-new-session" onClick={newSession} className="btn-primary" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.7rem', marginBottom: '1rem' }}><Plus size={16} /> New conversation</button>
          <ul data-testid="chat-session-list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.35rem' }}>
            {sessions.map((s) => (
              <li key={s.id}>
                <button type="button" onClick={() => setActive(s.id)} data-testid={`chat-session-${s.id}`} style={{ width: '100%', textAlign: 'left', padding: '0.6rem 0.75rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', backgroundColor: s.id === active ? 'rgba(44,90,160,0.12)' : 'transparent', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</button>
              </li>
            ))}
          </ul>
          <Link to="/portal" style={{ display: 'block', marginTop: '1.5rem', fontSize: '0.85rem', color: 'var(--brand-primary)' }}>← Back to portal</Link>
        </aside>
        <main style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem 2rem', display: 'grid', gap: '1rem', alignContent: 'start' }} data-testid="chat-messages">
            {messages.length === 0 && (
              <div style={{ maxWidth: '620px', margin: '3rem auto', textAlign: 'center', color: 'var(--text-secondary)' }}>
                <Bot size={40} color="var(--brand-primary)" />
                <h1 className="heading-3" style={{ margin: '1rem 0 0.5rem' }}>Ask about California legal procedures</h1>
                <p className="body-small" style={{ margin: 0 }}>Example: "How do I serve a small claims defendant who works from home?" or "What form proves I served a summons?" General information only - not legal advice.</p>
              </div>
            )}
            {messages.map((m, i) => <Bubble key={i} role={m.role} content={m.content} streaming={streaming && i === messages.length - 1 && m.role === 'assistant'} />)}
            <div ref={bottomRef} />
          </div>
          {error && <p data-testid="chat-error" role="alert" style={{ margin: '0 2rem 0.5rem', color: '#c53030', fontSize: '0.9rem' }}>{error}</p>}
          <form onSubmit={send} data-testid="chat-form" style={{ display: 'flex', gap: '0.75rem', padding: '1rem 2rem 1.5rem', borderTop: '1px solid var(--border-medium)', backgroundColor: 'var(--bg-card)' }}>
            <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your question..." maxLength={4000} style={{ ...inputStyle, flex: 1 }} disabled={streaming} />
            <button type="submit" className="btn-primary" data-testid="chat-send" disabled={streaming || !input.trim()} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.75rem 1.25rem' }}><Send size={16} /> Send</button>
          </form>
        </main>
      </div>
      <style>{`
        .chat-markdown p { margin: 0 0 0.6rem; } .chat-markdown p:last-child { margin-bottom: 0; }
        .chat-markdown h1, .chat-markdown h2, .chat-markdown h3 { font-size: 1rem; margin: 0.8rem 0 0.4rem; color: var(--brand-primary); }
        .chat-markdown ul, .chat-markdown ol { margin: 0.3rem 0 0.6rem 1.2rem; padding: 0; } .chat-markdown li { margin-bottom: 0.25rem; }
        .chat-markdown a { color: var(--brand-primary); }
        @media (max-width: 800px) { .assistant-layout { grid-template-columns: 1fr !important; } .assistant-layout aside { display: none; } }
      `}</style>
    </div>
  );
};

export default AssistantPage;
